import React, { useEffect, useState, useCallback, useLayoutEffect, Suspense } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { ProductDetailsPage } from '@unbridaled/search-sdk'
import { Preloader } from 'lib/components/preloader'
import StoreFunctions from 'stores/storeFunctions'
import { useWishlist } from 'utils/use-wishlist'
import { ErrorModal } from 'lib/components/modals/ErrorModal'
import { ConfirmationModal } from 'lib/components/modals/ConfirmationModal'
import { DASOrderSuccessfullModal } from 'lib/components/modals/DASOrderSuccessfullModal'
import { Roles } from 'constants/roles'
import { truncateCurrencyAmount } from 'constants/currencies'
import logger from 'utils/logger'
// import 'lib/App.css'
import { DiamondPageState, ProductDetailsPageProps, RouteParams } from 'types/interfaces'
import { useAppContext } from 'lib/context/AppContext'
import SimpleStore from 'stores/simpleStore'
import { getSearchSdkLocale } from 'i18n'

export const GemstonePage: React.FC<any> = props => {
  const { diamondStore, appConfig, currencyInfo } = useAppContext()
  const history = useHistory()
  const storeFunctions = new StoreFunctions()
  const simpleStore = useState(() => new SimpleStore())[0]

  const { id } = useParams<RouteParams>()
  const isCustomer =
    storeFunctions.getUserRole() === Roles.Customer &&
    !window.location.pathname.includes('merchant/gemstone')
  const [role] = useState(
    storeFunctions.getUserRole() === Roles.Merchant &&
      window.location.pathname.includes('merchant/gemstone')
      ? Roles.Merchant
      : Roles.Customer
  )
  const [isMemoMode] = useState(storeFunctions.isMemoMode())
  const [v360PlayerUrl, setV360PlayerUrl] = useState('')
  const [shopName, setShopName] = useState(null)
  const [shopCurrency, setShopCurrency] = useState<string | null>(null)
  const [pageReady, setPageReady] = useState(false)
  const [config] = useState({
    sourceTypes: appConfig?.source_types,
    advancedSearchCartEnabled: appConfig?.advanced_search_cart_enabled,
    ringSizeRegion: appConfig?.ring_size_region
  })
  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const [error, setError] = useState<DiamondPageState['error'] | null>(null)
  const [selectedGems, setSelectedGems] = useState<DiamondPageState['selectedGems']>([])
  const [selectedGemsOrderType, setSelectedGemsOrderType] = useState('i')

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [dasOrderSuccessfullModalOpen, setDasOrderSuccessfullModalOpen] = useState(false)

  const normalizeRetailPrice = useCallback((value: unknown): number => {
    const parsed = typeof value === 'string' ? Number(value.replace(/[\,]/g, '')) : Number(value)
    if (!Number.isFinite(parsed)) return 0
    return Math.round(parsed * 100) / 100
  }, [])

  const mapProductForDisplay = useCallback(
    (product: any): any => {
      if (!product) return product

      const isRegularMode = role !== Roles.Merchant && !isMemoMode
      const shopifyRate =
        shopCurrency && currencyInfo.currency !== shopCurrency
          ? window.Shopify?.currency?.rate || 1
          : 1
      const storeRetailPrice = normalizeRetailPrice(product?.retail_price)

      if (isRegularMode) {
        const converted = truncateCurrencyAmount(storeRetailPrice * shopifyRate)
        console.log(
          `[price][gemstone] ${shopCurrency || 'base'} ${storeRetailPrice} -> ${currencyInfo.currency} ${converted} @ Shopify rate ${shopifyRate}`
        )
        return {
          ...product,
          retail_price: converted,
          currency_code: currencyInfo.currency
        }
      }

      // Merchant/memo (double) mode: keep retail_price in the shop currency for
      // the primary line; the customer-currency (second) line uses the Shopify
      // presentment rate, matching the other surfaces (not DAS's rate).
      const cartConverted = truncateCurrencyAmount(storeRetailPrice * shopifyRate)
      console.log(
        `[price][gemstone][double] ${shopCurrency || 'base'} ${storeRetailPrice} -> ${currencyInfo.currency} ${cartConverted} @ Shopify rate ${shopifyRate}`
      )
      return {
        ...product,
        retail_price: storeRetailPrice,
        cart_price: cartConverted,
        currency_code: shopCurrency || product?.currency_code || currencyInfo.currency
      }
    },
    [role, isMemoMode, shopCurrency, currencyInfo.currency, normalizeRetailPrice]
  )

  const fetchShopName = async (): Promise<void | null> => {
    try {
      const shopInfo = await diamondStore.getShopName()
      setShopName(shopInfo.shopName)
    } catch (e) {
      logger.error('Failed to get shop name')
      return null
    }
  }

  const getProducts = async (params: { [key: string]: any }): Promise<any> => {
    params.currency_code = shopCurrency || currencyInfo.currency
    const result = await diamondStore.runFilter(params, 'gemstone_search')
    if (!result) return result
    return {
      ...result,
      gems: (result.gems || []).map(mapProductForDisplay)
    }
  }

  const fetchShopCurrency = async (): Promise<void | null> => {
    try {
      const currencyData = await simpleStore.ajax('GET', 'shop_currency')
      if (currencyData?.currency) {
        setShopCurrency(currencyData.currency)
        return
      }
      throw new Error('Failed to get shop currency')
    } catch (e) {
      logger.error('Failed to get shop currency')
      return null
    }
  }

  useEffect(() => {
    const loadPageData = async (): Promise<void> => {
      await Promise.all([fetchShopName(), fetchShopCurrency()])
      setPageReady(true)
    }

    loadPageData()
  }, [])

  useLayoutEffect(() => {
    setV360PlayerUrl(`${process.env.VIEWER_URL}/api/360player`)
  }, [])

  const closeErrorModal = (): void => {
    setError(null)
    setErrorModalOpen(false)
  }

  const closeConfirmationModal = (): void => {
    setConfirmationModalOpen(false)
  }

  const closeDASOrderSuccessfullModal = (): void => {
    setDasOrderSuccessfullModalOpen(false)
  }

  const handleAddToCart = async (gems: { [key: string]: any }[]): Promise<void> => {
    const result = await storeFunctions.addToCart(gems, {}, 'gemstone_search', 'gemstone')
    if (!result.success) {
      setError({
        title: result.error?.errorTitle || '',
        message: result.error?.errorBody || ''
      })
      setErrorModalOpen(true)
      return
    }
    window.location.href = '/cart'
  }

  const handleAddToDAS = useCallback(async (): Promise<void> => {
    const result = await storeFunctions.addToDAS({
      diamonds: selectedGems,
      type: selectedGemsOrderType,
      shop: shopName || '',
      searchType: "gemstone_search"
    })
    if (!result.success) {
      setConfirmationModalOpen(false)
      setError({
        title: result.error?.errorTitle || '',
        message: result.error?.errorBody || ''
      })
      setErrorModalOpen(true)
      return
    }
    setConfirmationModalOpen(false)
    setDasOrderSuccessfullModalOpen(true)
  }, [selectedGems, selectedGemsOrderType, shopName])

  const handleDASOrdering = (diamondsData: Array<any>, orderType: string): void => {
    const gems = Array.isArray(diamondsData) ? diamondsData : [diamondsData]
    setSelectedGems(gems)
    setSelectedGemsOrderType(orderType)
    setConfirmationModalOpen(true)
  }

  const wishlistProps = useWishlist('gemstone')

  const gemstoneSearchProps: ProductDetailsPageProps = {
    locale: getSearchSdkLocale(),
    data: { sku: id },
    productType: 'gemstone',
    shareProps: { hidden: true },
    role: role,
    isCustomer: isCustomer,
    isOnboarded: true,
    isActivated: true,
    merchant: { company_name: shopName || '', first_name: '' },
    ...(config.advancedSearchCartEnabled ? { onAddCartItem: handleAddToCart } : {}),
    ...(isMemoMode
      ? {
          onAddInStoreItem: (item: Array<any>): void => handleDASOrdering(item, 'i'),
          onAddMemoItem: (item: Array<any>): void => handleDASOrdering(item, 'm')
        }
      : {}),
    ...(process.env.VIEWER_URL ? { viewerUrl: process.env.VIEWER_URL } : {}),
    v360PlayerUrl: v360PlayerUrl,
    nativeV360PlayerUrl: `${location.origin}/apps/ub/360player`,
    hideMatchedDiamonds: true,
    api: { getProducts },
    currencyProps: {
      mode: role === Roles.Merchant || isMemoMode ? 'double' : 'single',
      currencies: currencyInfo.currencies,
      currencyCode: currencyInfo.currency,
      // Second (customer-currency) line = retail_price x this rate. Use the Shopify
      // presentment rate so it matches checkout/other surfaces (not DAS's rate). In
      // single mode retail_price is already converted, so rate is 1 (no re-multiply).
      currencyRate:
        role === Roles.Merchant || isMemoMode
          ? shopCurrency && currencyInfo.currency !== shopCurrency
            ? window.Shopify?.currency?.rate || 1
            : 1
          : 1
    },
    onBack: (): void => {
      history.push('/apps/ub/gemstones')
    },
    ringSizeProps: {
      sizeRegion: config.ringSizeRegion
    }
  }

  if (role === Roles.Merchant || isMemoMode) {
    gemstoneSearchProps.wishlistProps = wishlistProps
  }

  return (
    <div className="unbridaled-single-diamond">
      <ErrorModal
        open={errorModalOpen}
        title={error?.title}
        message={error?.message}
        onClose={closeErrorModal}
      />
      <ConfirmationModal
        open={confirmationModalOpen}
        stoneType="gemstone"
        gems={selectedGems}
        orderType={selectedGemsOrderType}
        config={config}
        currencyInfo={currencyInfo}
        onConfirm={handleAddToDAS}
        onClose={closeConfirmationModal}
      />
      <DASOrderSuccessfullModal
        open={dasOrderSuccessfullModalOpen}
        gems={selectedGems}
        orderType={selectedGemsOrderType}
        config={config}
        currencyInfo={currencyInfo}
        onClose={closeDASOrderSuccessfullModal}
      />
      <Suspense fallback={<Preloader preloaderClass="unbridaled-full-page-preloader" />}>
        {pageReady ? (
          <ProductDetailsPage
            {...gemstoneSearchProps}
          />
        ) : (
          <Preloader preloaderClass="unbridaled-full-page-preloader" />
        )}
      </Suspense>
    </div>
  )
}

export default GemstonePage
